import * as Yup from "yup";
import { TReactSetState, TUseTranslationTfn } from "../../data/AppType";
import {
  handleError,
  prepareMessageFromParams,
  sweetAlertError,
  sweetAlertSuccess,
  toastError,
} from "../../utils/AppFunctions";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  CREATE_TAX_GROUP,
  GET_TAX_COMPONENT_DDL,
  GET_TAX_GROUP_BY_ID,
  UPDATE_TAX_GROUP,
} from "../../services/Endpoints";
import { ICStateOState } from "./TaxGroupsViewController";
import {
  SYSTEM_TAX_CATEGORY_ENUM,
  TAX_STATE,
} from "../../utils/AppEnumerations";

export interface ITaxGroupForm {
  id?: string;
  name: string;
  company_state: (string | undefined)[];
  other_state: (string | undefined)[];
  tax_component?: ICStateOState[];
  created_by?: string;
  created_date?: string;
  modified_by?: string;
  modified_date?: string;
}

export const TAX_GROUPE_INITIAL_VALUE = {
  name: "",
  company_state: [undefined],
  other_state: [undefined],
};

export interface ITaxComponentsValue {
  data_value: string;
  display_value: string;
  rate: number;
  category_name: string;
}

export const taxGroupValidationSchema = (t: TUseTranslationTfn) =>
  Yup.object().shape({
    name: Yup.string()
      .required(
        prepareMessageFromParams(t("error_message_required"), [
          ["fieldName", t("name")],
        ]),
      )
      .max(
        50,
        prepareMessageFromParams(t("error_message_max_length"), [
          ["fieldName", t("name")],
          ["max", "50"],
        ]),
      )
      .min(
        2,
        prepareMessageFromParams(t("error_message_min_length"), [
          ["fieldName", t("name")],
          ["min", "2"],
        ]),
      ),
  });

export const fetchTaxGroupById = async (
  id: string,
  handleClose: (status: boolean) => void,
  setTaxGroups: TReactSetState<ITaxGroupForm | undefined>,
) => {
  try {
    const taxGroupFieldData = await GET_TAX_GROUP_BY_ID(id);
    if (
      taxGroupFieldData &&
      taxGroupFieldData.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      setTaxGroups(taxGroupFieldData.data);
    } else {
      handleClose(false);
      sweetAlertError({
        message: taxGroupFieldData.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleClose(false);
    handleError(e, "fetchTaxGroupById");
  }
};

export const handleSubmitTaxGroup = async (
  values: ITaxGroupForm,
  setLoading: TReactSetState<boolean>,
  id: string | undefined,
  handleClose: (status: boolean) => void,
) => {
  setLoading(true);
  try {
    let resultTaxGroup;
    if (id) {
      resultTaxGroup = await UPDATE_TAX_GROUP(values, id);
    } else {
      resultTaxGroup = await CREATE_TAX_GROUP(values);
    }
    if (resultTaxGroup && resultTaxGroup.code === DEFAULT_STATUS_CODE_SUCCESS) {
      sweetAlertSuccess({
        message: resultTaxGroup.message,
      });
      return handleClose(true);
    } else {
      sweetAlertError({
        message: resultTaxGroup?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      });
    }
  } catch (e: any) {
    handleError(e, "handleSubmitTaxGroup");
  } finally {
    setLoading(false);
  }
};

function sortStateArrays(
  mainArray: ITaxComponentsValue[],
  idArray: string[],
  stateType: TAX_STATE,
) {
  const priorityIdSet = new Set(idArray);

  const isValidCategory = (category: string) => {
    if (stateType === TAX_STATE.CompanyState) {
      return category !== SYSTEM_TAX_CATEGORY_ENUM.IGST;
    }
    return (
      category !== SYSTEM_TAX_CATEGORY_ENUM.SGST &&
      category !== SYSTEM_TAX_CATEGORY_ENUM.CGST
    );
  };

  const matchingItems: ITaxComponentsValue[] = [];
  const nonMatchingItems: ITaxComponentsValue[] = [];

  for (const item of mainArray) {
    if (!isValidCategory(item.category_name)) continue;

    if (priorityIdSet.has(item.data_value)) {
      matchingItems.push(item);
    } else {
      nonMatchingItems.push(item);
    }
  }

  return [...matchingItems, ...nonMatchingItems];
}
export const getTaxComponentsDdl = async (
  selectCompanyState: string[],
  setTaxGroupsCompanyState: TReactSetState<ITaxComponentsValue[]>,
  selectOtherState: string[],
  setTaxGroupsOtherState: TReactSetState<ITaxComponentsValue[]>,
  setLoading: TReactSetState<boolean>,
) => {
  try {
    const taxComponentsDDLList = await GET_TAX_COMPONENT_DDL();
    if (taxComponentsDDLList?.code === DEFAULT_STATUS_CODE_SUCCESS) {
      if (taxComponentsDDLList.data) {
        setTaxGroupsCompanyState(
          sortStateArrays(
            taxComponentsDDLList.data,
            selectCompanyState,
            TAX_STATE.CompanyState,
          ),
        );

        setTaxGroupsOtherState(
          sortStateArrays(
            taxComponentsDDLList.data,
            selectOtherState,
            TAX_STATE.OtherState,
          ),
        );
      } else {
        setTaxGroupsCompanyState([]);
        setTaxGroupsOtherState([]);
      }
    } else {
      toastError(
        taxComponentsDDLList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED,
      );
    }
  } catch (e) {
    handleError(e, "getTaxComponentsDdl");
  } finally {
    setLoading(false);
  }
};
